import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useApiClient } from '../../../../helpers/apiClient.helper';
import { logger } from '../../../helpers/logger.helper';
import { syncCoinsCheckoutSession } from '../../helpers/coinsApi';
import { useEconomyStore } from '../../../shared/stores/economyStore';
import { STRIPE_PUBLISHABLE_KEY_GENERATED } from '../../stripe-config.generated';

// CI: scripts/inject-stripe-env.js overwrites the literal; local: usually empty + EXPO_PUBLIC_* in .env.
// Metro on CI does not reliably inline EXPO_PUBLIC_*, so prefer the generated literal and fall back to env.
const generatedKey = String(STRIPE_PUBLISHABLE_KEY_GENERATED ?? '').trim();
const publishableKey = (
  generatedKey || (process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY ?? '')
).trim();

// Lazy + memoized: only call loadStripe when the overlay actually renders AND we have a key.
// Never call loadStripe('') — an empty key throws a Stripe IntegrationError as an unhandled
// promise rejection that fires on every coin-store open (the prod web error-rate alert).
let stripePromiseSingleton: ReturnType<typeof loadStripe> | null = null;
function getStripePromise() {
  if (!publishableKey) return null;
  if (!stripePromiseSingleton) {
    stripePromiseSingleton = loadStripe(publishableKey);
  }
  return stripePromiseSingleton;
}

export interface StripeCheckoutOverlayProps {
  clientSecret: string;
  sessionId: string;
  onComplete: (awarded: number) => void;
  onCancel: () => void;
}

export function StripeCheckoutOverlay({ clientSecret, sessionId, onComplete, onCancel }: StripeCheckoutOverlayProps) {
  const apiClient = useApiClient();
  const completedRef = useRef(false);
  const stripePromise = getStripePromise();

  const handleComplete = useCallback(async () => {
    if (completedRef.current) return;
    completedRef.current = true;
    try {
      const { coins, awarded } = await syncCoinsCheckoutSession(
        apiClient as (endpoint: string, options?: Record<string, unknown>) => Promise<{ status: number; data: unknown }>,
        sessionId,
      );
      // Capture pre-sync balance so we can derive the awarded amount when the
      // webhook processed the session first (idempotency guard returns awarded=0).
      const prevCoins = useEconomyStore.getState().coins;
      useEconomyStore.getState().syncCoins(coins);
      const effectiveAwarded = awarded > 0 ? awarded : Math.max(0, coins - prevCoins);
      onComplete(effectiveAwarded);
    } catch {
      onComplete(0);
    }
  }, [apiClient, sessionId, onComplete]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    if (!stripePromise) {
      logger.warn(
        'Stripe publishable key missing — web checkout unavailable. Production builds need EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY (GitHub Actions secret EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY_PROD); inject-stripe-env runs before the bundle.',
      );
    }
  }, [stripePromise]);

  const options = { clientSecret, onComplete: handleComplete };

  // Portal to document.body so React Native Modal's stacking context can't block it.
  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'rgba(0,0,0,0.75)',
      }}
    >
      {/* Header bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          backgroundColor: '#111827',
          flexShrink: 0,
        }}
      >
        <span style={{ color: '#F9FAFB', fontSize: 16, fontWeight: 600 }}>Secure checkout</span>
        <button
          onClick={onCancel}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#9CA3AF',
            fontSize: 22,
            lineHeight: 1,
            padding: '4px 8px',
          }}
          aria-label="Close checkout"
        >
          ✕
        </button>
      </div>

      {/* Stripe Embedded Checkout */}
      <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#ffffff' }}>
        {stripePromise ? (
          <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              padding: 32,
              textAlign: 'center',
              color: '#374151',
            }}
          >
            <span style={{ fontSize: 16, fontWeight: 600 }}>Checkout is temporarily unavailable</span>
            <span style={{ fontSize: 14, color: '#6B7280' }}>
              Card payments aren’t available right now. Please try again later.
            </span>
            <button
              onClick={onCancel}
              style={{
                marginTop: 8,
                padding: '10px 20px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                backgroundColor: '#111827',
                color: '#F9FAFB',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
